import axios from "axios";
import moment from "moment-timezone";
import { PDFDocument, PDFTextField, PDFCheckBox, StandardFonts, rgb } from "pdf-lib";
import { pdfjs } from "react-pdf";
import environment from "../config/environment.js";
import {
  AGENT_AGREEMENT_PDF_URL,
  AGENT_AGREEMENT_BROKER_NAME,
  AGENT_AGREEMENT_SALESPERSON_FIELD,
  AGENT_AGREEMENT_DATE_FIELD_NAMES,
  AGENT_AGREEMENT_AUTO_AGENT_NAME_FIELDS,
  AGENT_AGREEMENT_AUTO_BROKER_NAME_FIELDS,
  AGENT_AGREEMENT_SIGNATURE_PAGE,
  AGENT_AGREEMENT_S3_UPLOAD_HELP,
} from "../constants/agentAgreement.js";
import { CURRENT_AGENT_AGREEMENT_VERSION } from "../constants/fees.js";
import user_service from "../Components/service/user_service.js";

const INK_COLOR = rgb(0.08, 0.09, 0.12);

/**
 * Labels printed to the left of the two broker "Date" rows in the signature
 * block. Body copy on earlier pages also mentions the Principal Broker, so
 * these anchors are only honored on the signature page.
 */
const BROKER_DATE_ANCHOR = /principal broker|brokers?'? representative|broker'?s signature/i;

/** Draw coordinates for the broker date rows when anchor detection misses them. */
const BROKER_DATE_FALLBACK_ROWS = [
  { x: 278, y: 405, size: 10 },
  { x: 278, y: 343, size: 10 },
];

/** Vertical slack used to tell whether a broker date row already has a mark. */
const BROKER_DATE_ROW_TOLERANCE = 12;

const todayFormatted = () => moment().format("M/D/YYYY");

if (typeof window !== "undefined" && pdfjs?.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

export function getAgentAgreementProxyUrl(pdfUrl = AGENT_AGREEMENT_PDF_URL) {
  return `${environment.apiUrl}/proxy-image?url=${encodeURIComponent(pdfUrl)}`;
}

export function toAgentAgreementErrorMessage(error, fallback = "Unable to complete signing. Please try again.") {
  const message = error?.response?.data?.message || error?.message || "";
  if (/failed to fetch|networkerror|cors|network request failed/i.test(message)) {
    return "Unable to load the agreement file. Please refresh and try again.";
  }
  return message || fallback;
}

export async function fetchAgentAgreementPdfBytes(pdfUrl = AGENT_AGREEMENT_PDF_URL) {
  const sources = [pdfUrl, getAgentAgreementProxyUrl(pdfUrl)];

  let lastError;
  for (const source of sources) {
    try {
      const response = await fetch(source);
      if (!response.ok) {
        lastError = new Error(
          response.status === 404 ? AGENT_AGREEMENT_S3_UPLOAD_HELP : "Unable to load the agreement file."
        );
        continue;
      }
      return response.arrayBuffer();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to load the agreement file. Please refresh and try again.");
}

export async function getAgentAgreementPageCount(pdfUrl = AGENT_AGREEMENT_PDF_URL) {
  const existingPdfBytes = await fetchAgentAgreementPdfBytes(pdfUrl);
  const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
  return existingPdfDoc.getPageCount();
}

function drawFittedImage(page, image, box) {
  if (!image || !box) {
    return;
  }

  const imageAspect = image.width / image.height;
  const boxAspect = box.width / box.height;
  let width = box.width;
  let height = box.height;

  if (imageAspect > boxAspect) {
    height = width / imageAspect;
  } else {
    width = height * imageAspect;
  }

  page.drawImage(image, {
    x: box.x,
    y: box.y,
    width,
    height,
  });
}

function resolveSignaturePageIndex(pageCount) {
  return Math.min(
    Math.max(AGENT_AGREEMENT_SIGNATURE_PAGE - 1, 0),
    Math.max(pageCount - 1, 0)
  );
}

/**
 * The broker rows carry the same signing date as the salesperson rows, so any
 * row left empty by anchor detection is backfilled from known coordinates.
 */
export function withBrokerDateFallbacks(dates, pageCount) {
  const signaturePageIndex = resolveSignaturePageIndex(pageCount);
  if (signaturePageIndex >= pageCount) {
    return dates;
  }

  const result = [...dates];

  for (const row of BROKER_DATE_FALLBACK_ROWS) {
    const alreadyPlaced = result.some(
      (mark) =>
        mark.pageIndex === signaturePageIndex &&
        Math.abs(mark.y - row.y) <= BROKER_DATE_ROW_TOLERANCE
    );
    if (!alreadyPlaced) {
      result.push({ pageIndex: signaturePageIndex, ...row });
    }
  }

  return result;
}

function getFallbackPlacements(pageCount) {
  const initials = [];
  const dates = [];

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
    initials.push({ pageIndex, x: 108, y: 32, width: 52, height: 18 });
    dates.push({ pageIndex, x: 214, y: 36, size: 9 });
  }

  const signaturePageIndex = resolveSignaturePageIndex(pageCount);

  return {
    initials: [
      ...initials,
      { pageIndex: 2, x: 123, y: 548, width: 52, height: 18 },
      { pageIndex: signaturePageIndex, x: 119, y: 134, width: 70, height: 18 },
      { pageIndex: pageCount - 1, x: 184, y: 615, width: 70, height: 18 },
      { pageIndex: signaturePageIndex, x: 72, y: 110, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 709, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 669, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 616, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 563, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 509, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 470, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 417, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 364, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 324, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 271, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 218, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 178, width: 28, height: 14 },
      { pageIndex: signaturePageIndex + 1, x: 72, y: 139, width: 28, height: 14 },
      { pageIndex: pageCount - 1, x: 72, y: 709, width: 28, height: 14 },
    ].filter((item) => item.pageIndex < pageCount),
    signatures: [{ pageIndex: signaturePageIndex, x: 72, y: 442, width: 160, height: 24 }],
    printedNames: [
      { pageIndex: 0, x: 72, y: 561, size: 10 },
      { pageIndex: signaturePageIndex, x: 72, y: 476, size: 11 },
    ].filter((item) => item.pageIndex < pageCount),
    dates: withBrokerDateFallbacks(
      [
        ...dates,
        { pageIndex: 0, x: 250, y: 582, size: 10 },
        { pageIndex: signaturePageIndex, x: 281, y: 474, size: 10 },
        { pageIndex: signaturePageIndex, x: 278, y: 440, size: 10 },
        { pageIndex: pageCount - 1, x: 110, y: 590, size: 10 },
      ].filter((item) => item.pageIndex < pageCount),
      pageCount
    ),
  };
}

/**
 * Maps the text items of a single page to initial, signature, printed-name, and
 * date placements. Exported so the anchor rules can be tested without pdf.js.
 */
export function collectPageMarkPlacements(items, { pageIndex, isSignaturePage = false } = {}) {
  const result = {
    initials: [],
    signatures: [],
    printedNames: [],
    dates: [],
  };

  const salespersonAnchors = items.filter(
    (item) => /printed name$/i.test(item.text) || (/signature$/i.test(item.text) && !/electronic/i.test(item.text))
  );
  const brokerAnchors = isSignaturePage
    ? items.filter((item) => BROKER_DATE_ANCHOR.test(item.text))
    : [];

  for (const item of items) {
    const { text, x, y, w } = item;
    if (!text || /electronic signatures/i.test(text)) {
      continue;
    }

    if (text === "Initial:" || text === "Initials:") {
      result.initials.push({
        pageIndex,
        x: x + w + 6,
        y: y - 3,
        width: 52,
        height: 18,
      });
    } else if (/^Initials?:\s*_+/i.test(text) || /salesperson initials/i.test(text)) {
      const underscoreIndex = text.search(/_+/);
      const ratio = underscoreIndex > 0 ? underscoreIndex / text.length : 0.55;
      result.initials.push({
        pageIndex,
        x: x + w * ratio,
        y: y - 3,
        width: 70,
        height: 18,
      });
    } else if (/_+\s*Initial/i.test(text)) {
      const underscoreIndex = text.search(/_+/);
      result.initials.push({
        pageIndex,
        x: x + (underscoreIndex / text.length) * w,
        y: y - 3,
        width: 52,
        height: 18,
      });
    } else if (/^_{2,}\s+\d+\./.test(text)) {
      result.initials.push({
        pageIndex,
        x,
        y: y - 2,
        width: 28,
        height: 14,
      });
    } else if (/signature$/i.test(text) && !/electronic/i.test(text)) {
      result.signatures.push({
        pageIndex,
        x,
        y: y + 10,
        width: 160,
        height: 24,
      });
    } else if (/printed name$/i.test(text)) {
      result.printedNames.push({
        pageIndex,
        x,
        y: y + 10,
        size: 11,
      });
    } else if ((text === "Date:" || text === "Date" || /^Date:\s*_+/i.test(text)) && y < 50) {
      result.dates.push({
        pageIndex,
        x: x + w + 6,
        y,
        size: 9,
      });
    } else if (text === "Date:" || text === "Date" || /^Date:\s*_+/i.test(text)) {
      const nearSalesperson = salespersonAnchors.some(
        (anchor) => Math.abs(anchor.y - y) < 16 && anchor.x < x
      );
      const nearBroker = brokerAnchors.some(
        (anchor) => Math.abs(anchor.y - y) < 20 && anchor.x < x
      );
      if (nearSalesperson || nearBroker || /^Date:\s*_+/i.test(text)) {
        const underscoreIndex = text.search(/_+/);
        result.dates.push({
          pageIndex,
          x: underscoreIndex > 0 ? x + (underscoreIndex / text.length) * w : x + w + 8,
          y: y + 8,
          size: 10,
        });
      }
    } else if (/entered into on/i.test(text) && /_+/.test(text)) {
      const underscoreIndex = text.search(/_+/);
      result.dates.push({
        pageIndex,
        x: x + (underscoreIndex / text.length) * w,
        y,
        size: 10,
      });
    } else if (/^_{10,}/.test(text) && /salesperson or associate/i.test(text)) {
      result.printedNames.push({
        pageIndex,
        x,
        y,
        size: 10,
      });
    }
  }

  return result;
}

async function findAgreementMarkPlacements(pdfBytes) {
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(pdfBytes) }).promise;
  const signaturePageIndex = resolveSignaturePageIndex(pdf.numPages);
  const result = {
    initials: [],
    signatures: [],
    printedNames: [],
    dates: [],
  };

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const items = content.items.map((item) => ({
      text: (item.str || "").trim(),
      x: item.transform[4],
      y: item.transform[5],
      w: item.width || 0,
    }));

    const pageIndex = pageNumber - 1;
    const pagePlacements = collectPageMarkPlacements(items, {
      pageIndex,
      isSignaturePage: pageIndex === signaturePageIndex,
    });

    result.initials.push(...pagePlacements.initials);
    result.signatures.push(...pagePlacements.signatures);
    result.printedNames.push(...pagePlacements.printedNames);
    result.dates.push(...pagePlacements.dates);
  }

  if (!result.signatures.length) {
    return getFallbackPlacements(pdf.numPages);
  }

  result.dates = withBrokerDateFallbacks(result.dates, pdf.numPages);

  return result;
}

async function embedPngFromDataUrl(pdfDoc, dataUrl) {
  if (!dataUrl) {
    return null;
  }

  const imageBytes = await fetch(dataUrl).then((res) => res.arrayBuffer());
  return pdfDoc.embedPng(imageBytes);
}

export async function buildSignedAgentAgreementPdf({
  pdfUrl = AGENT_AGREEMENT_PDF_URL,
  fieldValues = {},
  agentName,
  initialsText = "",
  initialsDataUrl,
  signatureDataUrl,
}) {
  const existingPdfBytes = await fetchAgentAgreementPdfBytes(pdfUrl);
  const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
  const pageCount = existingPdfDoc.getPageCount();
  const font = await existingPdfDoc.embedFont(StandardFonts.TimesRomanItalic);
  const printedNameFont = await existingPdfDoc.embedFont(StandardFonts.TimesRoman);
  const dateText = todayFormatted();
  const resolvedInitials =
    (initialsText || "").trim() ||
    (agentName || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

  const signatureImage = await embedPngFromDataUrl(existingPdfDoc, signatureDataUrl);
  const initialsImage = await embedPngFromDataUrl(existingPdfDoc, initialsDataUrl);

  let placements;
  try {
    placements = await findAgreementMarkPlacements(existingPdfBytes);
  } catch (error) {
    placements = getFallbackPlacements(pageCount);
  }

  for (const box of placements.initials) {
    const page = existingPdfDoc.getPage(box.pageIndex);
    if (initialsImage) {
      drawFittedImage(page, initialsImage, box);
    } else if (resolvedInitials) {
      page.drawText(resolvedInitials, {
        x: box.x,
        y: box.y + 2,
        size: Math.min(11, box.height),
        font,
        color: INK_COLOR,
      });
    }
  }

  if (signatureImage) {
    for (const box of placements.signatures) {
      drawFittedImage(existingPdfDoc.getPage(box.pageIndex), signatureImage, box);
    }
  }

  const salespersonName =
    fieldValues[AGENT_AGREEMENT_SALESPERSON_FIELD] || agentName || "";

  for (const mark of placements.printedNames) {
    if (!salespersonName) {
      continue;
    }
    existingPdfDoc.getPage(mark.pageIndex).drawText(salespersonName, {
      x: mark.x,
      y: mark.y,
      size: mark.size || 10,
      font: printedNameFont,
      color: INK_COLOR,
    });
  }

  for (const mark of placements.dates) {
    existingPdfDoc.getPage(mark.pageIndex).drawText(dateText, {
      x: mark.x,
      y: mark.y,
      size: mark.size || 9,
      font: printedNameFont,
      color: INK_COLOR,
    });
  }

  try {
    const form = existingPdfDoc.getForm();
    const fieldNames = form.getFields().map((field) => field.getName());

    for (const fieldName of fieldNames) {
      const field = form.getField(fieldName);
      if (!field) {
        continue;
      }

      if (field instanceof PDFTextField) {
        const textField = form.getTextField(fieldName);
        let value = fieldValues[fieldName] || "";

        if (fieldName === AGENT_AGREEMENT_SALESPERSON_FIELD) {
          value = salespersonName;
        } else if (AGENT_AGREEMENT_AUTO_AGENT_NAME_FIELDS.includes(fieldName)) {
          value = agentName || salespersonName;
        } else if (AGENT_AGREEMENT_AUTO_BROKER_NAME_FIELDS.includes(fieldName)) {
          value = AGENT_AGREEMENT_BROKER_NAME;
        } else if (AGENT_AGREEMENT_DATE_FIELD_NAMES.includes(fieldName)) {
          value = dateText;
        }

        textField.setText(value);
      } else if (field instanceof PDFCheckBox) {
        const checkbox = form.getCheckBox(fieldName);
        if (fieldValues[fieldName]) {
          checkbox.check(fieldValues[fieldName]);
        }
      }
    }
  } catch {
    // Flat Word-exported PDFs have no fillable fields.
  }

  const modifiedPdfBytes = await existingPdfDoc.save();
  return new Blob([modifiedPdfBytes], { type: "application/pdf" });
}

export async function uploadSignedAgentAgreement({
  contactId,
  paperworkId,
  signedPdfBlob,
  fileName = "broker-agent-agreement-signed.pdf",
}) {
  const pdfFormData = new FormData();
  pdfFormData.append("file", signedPdfBlob, fileName);

  const uploadResponse = await axios.post(environment.uploadUrl, pdfFormData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const documentUrl = uploadResponse.data;

  await user_service.contactUpdate(contactId, {
    onboard_signature: documentUrl,
    agent_agreement_version: CURRENT_AGENT_AGREEMENT_VERSION,
  });

  if (paperworkId) {
    await user_service.agentpaperworkDocument({
      agentId: contactId,
      paperwork_id: paperworkId,
      documenturl: documentUrl,
    });
  }

  return documentUrl;
}

export async function signAndUploadAgentAgreement({
  contactId,
  paperworkId,
  agentName,
  fieldValues,
  initialsText,
  initialsDataUrl,
  signatureDataUrl,
  pdfUrl = AGENT_AGREEMENT_PDF_URL,
}) {
  const signedPdfBlob = await buildSignedAgentAgreementPdf({
    pdfUrl,
    fieldValues,
    agentName,
    initialsText,
    initialsDataUrl,
    signatureDataUrl,
  });

  const documentUrl = await uploadSignedAgentAgreement({
    contactId,
    paperworkId,
    signedPdfBlob,
  });

  return documentUrl;
}
